import React, { useEffect, useState } from 'react';
import { DeleteOutlined, EditOutlined, PlusOutlined, DownloadOutlined } from '@ant-design/icons';
import { Col, Popconfirm, Checkbox } from 'antd';
import { useTranslation } from 'react-i18next';
import Box from '~/components/Box';
import Button from '~/components/Button';
import Pagination from '~/components/Pagination';
import Row from '~/components/Row';
import { Table, TableActions } from '~/components/Table';
import DrawingForm from './form';
import Filter from './filter';
import { paginationDrawings, deleteDrawing } from '~/services/hooks/drawings';
import { paginationEstimates, createEstimate } from '~/services/hooks/estimates';
import { updateDrawing } from '~/services/hooks/drawings';
import { downloadFile } from '~/services/hooks/files';
import moment from 'moment';

export default function Drawings({ project, customer }) {
  const estimateInitialValues = {
    id: null,
    sent: null,
    expireTime: 0,
    project: project,
    items: [],
  };

  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [meta, setMeta] = useState({});
  const [tableData, setTableData] = useState([]);
  const [sortBy, setSortBy] = useState('id');
  const [sortDirection, setSortDirection] = useState('DESC');
  const [filters, setFilters] = useState([]);
  const [estimate, setEstimate] = useState(estimateInitialValues);

  const fetchDataEstimate = async () => {
    setLoading(true);
    const data = await paginationEstimates({
      page: 0,
      maxSize: true,
      filters: [{ columnJoin: 'project', field: 'id', value: project.id, restriction: 'EQUAL' }],
    });
    if (data.numberOfElements === 0) {
      const newEstimate = await createEstimate(estimateInitialValues);
      setEstimate(newEstimate);
    } else {
      setEstimate(data.content[0]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchDataEstimate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async page => {
    setLoading(true);
    const data = await paginationDrawings({
      page: page - 1,
      sortBy,
      sortDirection,
      filters: [...filters, { columnJoin: 'estimate', field: 'id', value: estimate?.id, restriction: 'EQUAL' }],
    });
    setTableData(data.content);
    const metaData = {
      first: data.first,
      last: data.last,
      numberOfElements: data.numberOfElements,
      pageNumber: data.pageable.pageNumber + 1,
      pageSize: data.pageable.pageSize,
      totalElements: data.totalElements,
    };
    setMeta(metaData);
    setLoading(false);
  };

  useEffect(() => {
    if (estimate.id !== null) {
      fetchData(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sortBy, estimate]);

  const handleDelete = async id => {
    await deleteDrawing(id);
    fetchData(1);
  };

  const handleEdit = record => {
    setSelectedRecord(record);
    setShowForm(true);
  };

  const onUpdateItem = item => {
    const index = tableData.findIndex(i => i.id === item.id);
    if (index > -1) {
      const newData = [...tableData];
      newData[index] = item;
      setTableData(newData);
    }
  };

  const style = {
    btnNew: {
      col: { marginLeft: 'auto', paddingBottom: '20px' },
      btn: { marginLeft: 'auto' },
    },
  };

  const tableColumns = [
    {
      title: t('screens:drawing.id'),
      dataIndex: 'id',
      key: 'id',
      align: 'left',
      sorter: true,
      width: 80,
      ellipsis: {
        showTitle: true,
      },
    },
    {
      title: t('screens:drawing.description'),
      dataIndex: 'description',
      key: 'description',
      align: 'left',
      sorter: true,
      ellipsis: {
        showTitle: true,
      },
    },
    {
      title: t('screens:drawing.showToVendor'),
      key: 'showToVendor',
      ellipsis: {
        showTitle: false,
      },
      align: 'center',
      width: '100px',
      render: record => (
        <Checkbox
          checked={record.showToVendor}
          onChange={async () => {
            await updateDrawing({ ...record, showToVendor: !record.showToVendor });
            fetchData(1);
          }}
        />
      ),
    },
    {
      title: t('screens:drawing.createDate'),
      dataIndex: 'audit',
      key: 'audit.createDate',
      align: 'left',
      width: 200,
      sorter: true,
      ellipsis: { showTitle: true },
      render: audit => <>{moment(audit.createdDate).format('YYYY/MM/DD HH:MM:ss')}</>, // render: (audit) => (<>{audit.createDate}</>)
    },
    {
      title: t('screens:drawing.actions'),
      key: 'actions',
      align: 'center',
      ellipsis: {
        showTitle: true,
      },
      className: 'table-action',
      render: record => (
        <TableActions>
          <Button onClick={async () => await downloadFile(record.arquive)} title={t('messages:download')}>
            <DownloadOutlined />
          </Button>
          <Button onClick={() => handleEdit(record)} title={t('messages:edit')}>
            <EditOutlined />
          </Button>
          <Popconfirm
            icon={false}
            title={t('messages:confirmDelete')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('messages:yes')}
            cancelText={t('messages:no')}
          >
            <Button title={t('messages:delete')}>
              <DeleteOutlined />
            </Button>
          </Popconfirm>
        </TableActions>
      ),
    },
  ];

  const handleTableChange = (pagination, filters, sorter) => {
    const sortOrder = sorter.order === 'ascend' ? '' : 'DESC';
    if (sorter.field !== sortBy || sortOrder !== sortDirection) {
      setSortBy(sorter.field);
      setSortDirection(sortOrder);
      fetchData(1);
    }
  };

  return (
    <>
      <Filter isLoading={loading} setFilters={setFilters} />
      <Box>
        <Row>
          <Col span={12} style={style.btnNew.col}>
            <Button
              loading={loading}
              color="primary"
              onClick={() => {
                setSelectedRecord(null);
                setShowForm(true);
              }}
              style={style.btnNew.btn}
            >
              <PlusOutlined />
              {t('screens:drawing.btnNew')}
            </Button>
          </Col>
        </Row>
        <Table
          bordered
          rowKey="id"
          pagination={false}
          loading={loading}
          columns={tableColumns}
          dataSource={[...tableData]}
          onChange={handleTableChange}
        />
        <Pagination
          onChange={value => {
            fetchData(value);
          }}
          meta={meta}
        />
        <DrawingForm
          estimate={estimate}
          selectedRecord={selectedRecord}
          visible={showForm}
          onClose={() => setShowForm(false)}
          onInsertItem={item => {
            setTableData([...tableData, item]);
          }}
          onUpdateItem={item => onUpdateItem(item)}
        />
      </Box>
    </>
  );
}
